import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaySuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("order_id") || params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [attempts, setAttempts] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const MAX = 15;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        setAttempts(n + 1);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.data.payment_status === "failed" || res.data.status === "expired") {
          setState("failed");
          return;
        }
        if (n >= MAX) {
          setState("failed");
          return;
        }
        timerRef.current = setTimeout(() => poll(n + 1), 1500);
      } catch (e) {
        if (n >= MAX) {
          setState("failed");
          return;
        }
        timerRef.current = setTimeout(() => poll(n + 1), 1500);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  const downloadPdf = async () => {
    if (!sessionId) return;
    setDownloading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/pdf/download/${sessionId}`, {
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `careerwisdom-journey-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded!");
    } catch (e) {
      toast.error("Could not download the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-[#F4F4F0] px-6" data-testid="pay-success-page">
      <div className="max-w-lg w-full bg-white border border-[#E5E5E0] rounded-sm p-8 md:p-10 fade-up">
        {state === "polling" && (
          <div className="text-center" data-testid="pay-polling">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7] mx-auto mb-5" />
            <div className="overline mb-2">Confirming payment</div>
            <h1 className="font-heading text-3xl font-black tracking-tight">Hang tight…</h1>
            <p className="mt-3 text-[#5C5C55]">Verifying your payment with Razorpay (attempt {attempts}/15).</p>
          </div>
        )}
        {state === "paid" && (
          <div className="text-center" data-testid="pay-success">
            <div className="w-14 h-14 bg-[#166534] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-white" strokeWidth={1.8} />
            </div>
            <div className="overline mb-2">Payment received</div>
            <h1 className="font-heading text-3xl font-black tracking-tight">You&apos;re all set.</h1>
            <p className="mt-3 text-[#5C5C55]">
              Your career journey PDF is ready. Download it below and share it with anyone who&apos;s helping you decide.
            </p>
            <button
              onClick={downloadPdf}
              disabled={downloading}
              data-testid="download-pdf-button"
              className="btn-primary mt-6 w-full justify-center"
            >
              {downloading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</>
              ) : (
                <><Download className="w-4 h-4" /> Download my PDF</>
              )}
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-3 text-sm text-[#5C5C55] hover:text-[#002FA7] transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              Back to dashboard
            </button>
          </div>
        )}
        {(state === "failed" || state === "expired") && (
          <div className="text-center" data-testid="pay-failed">
            <div className="w-14 h-14 bg-[#FF2A00] flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-7 h-7 text-white" strokeWidth={1.8} />
            </div>
            <div className="overline mb-2">Payment {state === "expired" ? "expired" : "issue"}</div>
            <h1 className="font-heading text-3xl font-black tracking-tight">
              {state === "expired" ? "Session expired" : "Couldn't confirm payment"}
            </h1>
            <p className="mt-3 text-[#5C5C55]">
              No charge was completed. Please try again from your dashboard.
            </p>
            <button onClick={() => navigate("/dashboard")} className="btn-primary mt-6 w-full justify-center">
              Back to dashboard
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
